import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useBanUser, useUnbanUser, useSearchUsersPaginated } from "@src/presentation/hooks/users"
import { useStats } from "@src/presentation/hooks/stats"
import { ToastService } from "@src/shared/services/toast.service"
import {
    Button,
    Card,
    CardBody,
    CardHeader,
    Chip,
    Input,
    Table,
    TableBody,
    TableCell,
    TableColumn,
    TableHeader,
    TableRow,
    Avatar,
    Dropdown,
    DropdownTrigger,
    DropdownMenu,
    DropdownItem,
    Skeleton,
} from "@heroui/react"
import {
    Search,
    UserPlus,
    MoreVertical,
    Eye,
    Edit,
    Ban,
    Shield,
    Mail,
    Users,
    CheckCircle,
    Clock,
    X
} from "lucide-react"

export default function AllUsersPage() {
    const navigate = useNavigate()
    const [searchQuery, setSearchQuery] = useState("")
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("")

    // Debounce search query
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery)
        }, 300)

        return () => clearTimeout(timer)
    }, [searchQuery])

    // Fetch users with infinite scroll
    const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useSearchUsersPaginated({
        query: debouncedSearchQuery || undefined,
        limit: 50
    })

    // Fetch stats
    const { data: stats, isLoading: statsLoading } = useStats()

    // Ban/unban mutations
    const { mutate: banUser } = useBanUser({
        onSuccess: () => {
            ToastService.success("User banned successfully")
        },
        onError: (error) => {
            ToastService.error(error.message || "Failed to ban user")
        }
    })

    const { mutate: unbanUser } = useUnbanUser({
        onSuccess: () => {
            ToastService.success("User unbanned successfully")
        },
        onError: (error) => {
            ToastService.error(error.message || "Failed to unban user")
        }
    })

    // Get users from paginated data
    const allUsers = data?.pages.flatMap((page) => page.users) || []

    // Get status based on user flags
    const getUserStatus = (user: typeof allUsers[0]) => {
        if (user.is_banned) return "banned"
        if (!user.is_active) return "inactive"
        return "active"
    }

    // Get status color
    const getStatusColor = (status: string) => {
        switch (status) {
            case "active":
                return "success"
            case "inactive":
                return "warning"
            case "banned":
                return "danger"
            default:
                return "default"
        }
    }

    // Get role color
    const getRoleColor = (role: string) => {
        switch (role.toLowerCase()) {
            case "admin":
            case "administrator":
                return "primary"
            case "editor":
            case "moderator":
                return "secondary"
            case "viewer":
            case "user":
                return "default"
            default:
                return "default"
        }
    }

    // Stats from API
    const dashboardStats = stats?.dashboard

    // Helper for stat card content
    const renderStatCard = (title: string, value: number | string, icon: React.ReactNode, cardClass: string, iconClass: string) => (
        <Card className={cardClass}>
            <CardBody className="p-3 sm:p-4">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-xs text-default-500 uppercase tracking-wide font-medium mb-1">
                            {title}
                        </p>
                        {statsLoading ? (
                            <Skeleton className="h-6 w-16 rounded" />
                        ) : (
                            <p className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                                {value}
                            </p>
                        )}
                    </div>
                    <div className={`hidden sm:flex h-12 w-12 items-center justify-center rounded-lg shadow-none ${iconClass}`}>
                        {icon}
                    </div>
                </div>
            </CardBody>
        </Card>
    )

    // Table content
    const tableContent = isLoading
        ? Array.from({ length: 5 }).map((_, index) => (
            <TableRow key={`skeleton-${index}`} className="border-y border-border min-h-[64px]">
                <TableCell>
                    <div className="flex items-center gap-3">
                        <Skeleton className="h-8 w-8 rounded-full" />
                        <div className="flex flex-col gap-2">
                            <Skeleton className="h-4 w-32" />
                            <Skeleton className="h-3 w-48" />
                        </div>
                    </div>
                </TableCell>
                <TableCell className="hidden md:table-cell">
                    <Skeleton className="h-6 w-16 rounded-full" />
                </TableCell>
                <TableCell className="hidden md:table-cell">
                    <Skeleton className="h-6 w-16 rounded-full" />
                </TableCell>
                <TableCell className="hidden md:table-cell">
                    <Skeleton className="h-4 w-20" />
                </TableCell>
                <TableCell className="hidden md:table-cell">
                    <Skeleton className="h-4 w-20" />
                </TableCell>
                <TableCell>
                    <Skeleton className="h-8 w-8 rounded" />
                </TableCell>
            </TableRow>
        ))
        : allUsers.length === 0
            ? [
                <TableRow key="empty">
                    <TableCell colSpan={6} className="text-center py-12">
                        <div className="flex flex-col items-center gap-2">
                            <Users className="h-12 w-12 text-default-400" />
                            <p className="text-default-500 font-medium">No users found</p>
                            <p className="text-default-400 text-sm">
                                {debouncedSearchQuery ? "Try adjusting your search criteria" : "Users will appear here once added"}
                            </p>
                        </div>
                    </TableCell>
                </TableRow>
            ]
            : allUsers.map((user) => (
                <TableRow
                    key={user.user_id}
                    className="cursor-pointer hover:bg-foreground/5 transition-colors border-y border-border min-h-[64px]"
                    onClick={() => navigate(`/users/${user.user_id}`)}
                >
                    <TableCell>
                        <div className="flex items-center gap-3">
                            <Avatar
                                name={user.display_name}
                                src={user.profile_picture_url || undefined}
                                className="flex-shrink-0 shadow-sm"
                                size="sm"
                                color={user.roles.length > 0 ? getRoleColor(user.roles[0].name) : "default"}
                            />
                            <div className="flex flex-col">
                                <span className="font-semibold text-foreground">
                                    {user.display_name}
                                </span>
                                <span className="text-xs text-default-500 flex items-center gap-1">
                                    <Mail className="h-3 w-3" />
                                    {user.email}
                                </span>
                            </div>
                        </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                        {user.roles.length > 0 ? (
                            <Chip
                                size="sm"
                                variant="flat"
                                color={getRoleColor(user.roles[0].name)}
                                startContent={<Shield className="h-3 w-3" />}
                                className="shadow-sm"
                            >
                                {user.roles[0].display_name}
                            </Chip>
                        ) : (
                            <span className="text-default-400 text-sm">No role</span>
                        )}
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                        <Chip
                            size="sm"
                            variant="flat"
                            color={getStatusColor(getUserStatus(user))}
                            className="shadow-sm"
                        >
                            {getUserStatus(user)}
                        </Chip>
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-default-500">
                        Never
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-default-500">
                        {new Date(user.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                        <Dropdown>
                            <DropdownTrigger>
                                <Button
                                    isIconOnly
                                    size="sm"
                                    variant="light"
                                >
                                    <MoreVertical className="h-4 w-4" />
                                </Button>
                            </DropdownTrigger>
                            <DropdownMenu aria-label="User actions">
                                <DropdownItem
                                    key="view"
                                    startContent={<Eye className="h-4 w-4" />}
                                    onPress={() => navigate(`/users/${user.user_id}`)}
                                >
                                    View Details
                                </DropdownItem>
                                <DropdownItem
                                    key="edit"
                                    startContent={<Edit className="h-4 w-4" />}
                                    onPress={() => navigate(`/users/${user.user_id}/edit`)}
                                >
                                    Edit User
                                </DropdownItem>
                                <DropdownItem
                                    key="divider"
                                    className="h-px my-1 bg-divider"
                                />
                                {getUserStatus(user) === "banned" ? (
                                    <DropdownItem
                                        key="unban"
                                        className="text-success"
                                        color="success"
                                        startContent={<Shield className="h-4 w-4" />}
                                        onPress={() => unbanUser(user.user_id)}
                                    >
                                        Unban User
                                    </DropdownItem>
                                ) : (
                                    <DropdownItem
                                        key="ban"
                                        className="text-danger"
                                        color="danger"
                                        startContent={<Ban className="h-4 w-4" />}
                                        onPress={() => banUser(user.user_id)}
                                    >
                                        Ban User
                                    </DropdownItem>
                                )}
                            </DropdownMenu>
                        </Dropdown>
                    </TableCell>
                </TableRow>
            ))

    return (
        <div className="min-h-screen bg-gradient-to-br from-background via-background to-default-50">
            {/* Header */}
            <header className="border-b border-border bg-background/80 backdrop-blur-xl supports-[backdrop-filter]:bg-background/60 sticky top-0 z-20">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col sm:flex-row min-h-[56px] sm:h-16 py-2 sm:py-0 items-start sm:items-center justify-between gap-2 sm:gap-0">
                        <div className="flex items-center gap-2 sm:gap-3">
                            <div className="flex h-8 sm:h-10 w-8 sm:w-10 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-primary-600 shadow-md">
                                <Users className="h-4 sm:h-5 w-4 sm:w-5 text-primary-foreground" />
                            </div>
                            <div>
                                <h1 className="text-lg sm:text-xl font-bold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                                    User Management
                                </h1>
                                <p className="text-xs text-default-500 font-medium hidden sm:block">
                                    Manage users and their access permissions
                                </p>
                            </div>
                        </div>
                        <Button
                            color="primary"
                            startContent={<UserPlus className="h-4 w-4" />}
                            onPress={() => navigate("/users/new")}
                            className="hover:scale-105 transition-transform font-semibold rounded w-full sm:w-auto"
                            size="sm"
                        >
                            <span className="hidden sm:inline">Add User</span>
                            <span className="sm:hidden">Add User</span>
                        </Button>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Stats Cards */}
                <div className="grid gap-4 grid-cols-2 lg:grid-cols-5 mb-6">
                    {renderStatCard(
                        "Total Users",
                        dashboardStats?.total_users || 0,
                        <Users className="h-6 w-6 text-primary-foreground" />,
                        "border shadow-none border-primary/20 rounded transition-shadow bg-gradient-to-br from-primary/10 to-primary/5",
                        "bg-gradient-to-br from-primary to-primary-600"
                    )}
                    {renderStatCard(
                        "Active",
                        dashboardStats?.active_users || 0,
                        <CheckCircle className="h-6 w-6 text-success-foreground" />,
                        "border shadow-none border-success/20 rounded transition-shadow bg-gradient-to-br from-success/10 to-success/5",
                        "bg-gradient-to-br from-success to-success-600"
                    )}
                    {renderStatCard(
                        "Inactive",
                        (dashboardStats?.total_users || 0) - (dashboardStats?.active_users || 0) - (dashboardStats?.banned_users || 0),
                        <Clock className="h-6 w-6 text-warning-foreground" />,
                        "border shadow-none border-warning/20 rounded bg-gradient-to-br from-warning/10 to-warning/5",
                        "bg-gradient-to-br from-warning to-warning-600"
                    )}
                    {renderStatCard(
                        "Banned",
                        dashboardStats?.banned_users || 0,
                        <Ban className="h-6 w-6 text-danger-foreground" />,
                        "border shadow-none border-danger/20 rounded bg-gradient-to-br from-danger/10 to-danger/5",
                        "bg-gradient-to-br from-danger to-danger-600"
                    )}
                    {renderStatCard(
                        "Verified",
                        dashboardStats?.verified_users || 0,
                        <Shield className="h-6 w-6 text-info-foreground" />,
                        "border shadow-none border-info/20 rounded bg-gradient-to-br from-info/10 to-info/5",
                        "bg-gradient-to-br from-info to-info-600"
                    )}
                </div>

                {/* Filters and Search */}
                <Card className="mb-6 bg-secondary rounded shadow-none border border-border">
                    <CardBody className="p-4">
                        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                            <Input
                                placeholder="Search by name, email, or phone number..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onClear={() => setSearchQuery("")}
                                startContent={<Search className="h-4 w-4 text-default-400" />}
                                endContent={searchQuery ? (
                                    <button
                                        type="button"
                                        onClick={() => setSearchQuery("")}
                                        className="text-default-400 hover:text-default-600 transition-colors"
                                    >
                                        <X className="h-4 w-4" />
                                    </button>
                                ) : null}
                                className="flex-1"
                                classNames={{
                                    input: "text-sm",
                                    inputWrapper: "rounded"
                                }}
                            />
                        </div>
                    </CardBody>
                </Card>

                {/* Users Table */}
                <Card className="border border-border rounded bg-secondary shadow-none">
                    <CardHeader className="border-b border-divider pb-3">
                        <div className="flex items-center gap-2">
                            <div className="p-2 rounded-lg bg-primary/10">
                                <Users className="h-4 w-4 text-primary" />
                            </div>
                            <h2 className="text-lg font-semibold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                                All Users ({dashboardStats?.total_users || allUsers.length})
                            </h2>
                        </div>
                    </CardHeader>
                    <CardBody className="p-0">
                        <div className="overflow-x-auto">
                            <Table
                                aria-label="Users table"
                                classNames={{
                                    wrapper: "shadow-none rounded-none",
                                    th: "bg-default-50 text-default-700 font-semibold uppercase text-xs tracking-wide",
                                    td: "text-sm"
                                }}
                            >
                                <TableHeader>
                                    <TableColumn>USER</TableColumn>
                                    <TableColumn className="hidden md:table-cell">ROLE</TableColumn>
                                    <TableColumn className="hidden md:table-cell">STATUS</TableColumn>
                                    <TableColumn className="hidden md:table-cell">LAST LOGIN</TableColumn>
                                    <TableColumn className="hidden md:table-cell">JOINED</TableColumn>
                                    <TableColumn>ACTIONS</TableColumn>
                                </TableHeader>
                                <TableBody>
                                    {tableContent}
                                </TableBody>
                            </Table>
                        </div>
                    </CardBody>

                    {/* Infinite Scroll Trigger */}
                    {hasNextPage && (
                        <div className="flex items-center justify-center py-8">
                            {isFetchingNextPage ? (
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
                                    Loading more users...
                                </div>
                            ) : (
                                <button
                                    onClick={() => fetchNextPage()}
                                    className="px-4 py-2 text-sm text-primary hover:text-primary/80 font-medium"
                                >
                                    Load more users
                                </button>
                            )}
                        </div>
                    )}

                    {!hasNextPage && allUsers.length > 0 && (
                        <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
                            You've reached the end
                        </div>
                    )}
                </Card>
            </main>
        </div>
    )
}
